import React from "react";
import { Filter, X } from "lucide-react";

/**
 * Generic header filter bar for ticket lists.
 *
 * Props:
 *   filters: { status, priority, plant, category, assignee, request_type, change_type, ... }
 *   onChange: (newFilters) => void
 *   options: array of filter spec objects:
 *     { key: 'status', label: 'Status', values: ['logged','assigned',...] }
 *     OR { key: 'assignee', label: 'Assignee', values: [{value, label}, ...] }
 *   onClear: optional reset handler
 */
export default function FilterBar({ filters = {}, onChange, options = [], onClear, search, onSearchChange, searchPlaceholder = "Search…" }) {
  function set(key, value) {
    const next = { ...filters };
    if (!value) delete next[key];
    else next[key] = value;
    onChange?.(next);
  }
  const activeCount = Object.keys(filters).filter((k) => filters[k]).length;
  return (
    <div className="card p-3 space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <Filter size={14} className="text-gray-500" />
        {options.map((o) => (
          <select
            key={o.key}
            value={filters[o.key] || ""}
            onChange={(e) => set(o.key, e.target.value)}
            className="text-sm border border-gray-200 rounded px-2 py-1 bg-white focus:outline-none focus:border-brand-500"
          >
            <option value="">{o.label}</option>
            {o.values.map((v) => {
              const val = typeof v === "object" ? v.value : v;
              const lbl = typeof v === "object" ? v.label : v;
              return <option key={val} value={val}>{lbl}</option>;
            })}
          </select>
        ))}
        {onSearchChange && (
          <input
            type="text"
            value={search || ""}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder={searchPlaceholder}
            className="flex-1 min-w-[180px] text-sm border border-gray-200 rounded px-3 py-1 bg-white focus:outline-none focus:border-brand-500"
          />
        )}
        {(activeCount > 0 || (search && search.length > 0)) && (
          <button
            type="button"
            onClick={() => { onClear?.(); onSearchChange?.(""); }}
            className="text-xs text-gray-500 hover:text-gray-800 flex items-center gap-1 ml-auto"
            title="Clear all filters"
          >
            <X size={12} /> Clear ({activeCount}{search ? "+1" : ""})
          </button>
        )}
      </div>
    </div>
  );
}

/** Apply filter object to a row. Returns true if row matches all active filters. */
export function matchFilters(row, filters) {
  for (const [k, v] of Object.entries(filters || {})) {
    if (!v) continue;
    let actual = row[k];
    if (k === "assignee") actual = row.assigned_to_id;
    if (k === "is_overdue") actual = row.is_overdue ? "yes" : "no";
    if (String(actual ?? "") !== String(v)) return false;
  }
  return true;
}
